import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import { BienestarAgendaSnippet } from './bienestar/BienestarAgenda';
import {
  BienestarCrossPaths,
  BienestarLandingHero,
  BienestarProfessionalBlock,
  BienestarStoreCta,
  useBienestarPageMeta,
} from './bienestar/BienestarShared';
import { BIENESTAR_LANDING_HERO, goContact, goReservar, navigateTo } from './bienestar/bienestarNav';
import './BienestarPage.css';

const BienestarConectaPage = () => {
  const { t } = useTranslation();

  useBienestarPageMeta('bienestarLandings.metaConectaTitle', 'bienestarLandings.metaConectaDesc');

  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  const conectaSessions = useMemo(() => (
    [1, 2, 3, 4, 5, 6, 7].map((n) => ({
      n: String(n).padStart(2, '0'),
      title: t(`bienestarHub.conectaSession${String(n).padStart(2, '0')}`),
      desc: t(`bienestarHub.conectaSession${String(n).padStart(2, '0')}Desc`),
    }))
  ), [t]);

  return (
    <div className="bienestar-page bienestar-page--landing">
      <Header />
      <BienestarLandingHero
        kickerKey="bienestarLandings.conectaKicker"
        titleKey="bienestarLandings.conectaHeroTitle"
        leadKey="bienestarLandings.conectaHeroLead"
        ctaKey="bienestarHub.conectaPriceCtaSingle"
        onCta={() => goReservar('conecta-sesion')}
        imageSrc={BIENESTAR_LANDING_HERO.conecta.src}
        fallbackSrc={BIENESTAR_LANDING_HERO.conecta.fallbackSrc}
      />

      <main className="bienestar-main bw-main">
        <div className="container bw-container">
          <section className="bw-section bw-conecta-block">
            <header className="bw-conecta-hero">
              <span className="bw-conecta-kicker">{t('bienestarHub.conectaKicker')}</span>
              <h2 className="bw-section-title bw-conecta-title">{t('bienestarHub.conectaTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.conectaLead')}</p>
            </header>

            <h3 className="bw-subheading">{t('bienestarHub.conectaForWhoTitle')}</h3>
            <div className="bw-two-col">
              <article className="bw-info-card">
                <div className="bw-info-icon"><i className="fas fa-brain" /></div>
                <h4>{t('bienestarHub.conectaPsyTitle')}</h4>
                <p>{t('bienestarHub.conectaPsyDesc')}</p>
              </article>
              <article className="bw-info-card">
                <div className="bw-info-icon"><i className="fas fa-route" /></div>
                <h4>{t('bienestarHub.conectaCoachTitle')}</h4>
                <p>{t('bienestarHub.conectaCoachDesc')}</p>
              </article>
            </div>

            <h3 className="bw-subheading">{t('bienestarHub.conectaProgramTitle')}</h3>
            <p className="bw-section-sub bw-section-sub--tight">{t('bienestarHub.conectaProgramSub')}</p>
            <div className="bw-conecta-sessions">
              {conectaSessions.map((s) => (
                <article key={s.n} className="bw-conecta-session-item">
                  <span className="bw-session-num">{s.n}</span>
                  <div>
                    <h4>{s.title}</h4>
                    <p>{s.desc}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="bw-pricing-row">
              <article className="bw-price-card">
                <span className="bw-price-label">{t('bienestarLandings.singleSessionLabel')}</span>
                <div className="bw-price-big">{t('bienestarHub.conectaPriceSingle')}<span>COP</span></div>
                <p>{t('bienestarHub.conectaPriceSingleDesc')}</p>
                <button type="button" className="bw-cta bw-cta--outline" onClick={() => goReservar('conecta-sesion')}>
                  {t('bienestarHub.conectaPriceCtaSingle')}
                </button>
              </article>
              <article className="bw-price-card bw-price-card--featured">
                <span className="bw-price-label">{t('bienestarLandings.program7Label')}</span>
                <div className="bw-price-big">{t('bienestarHub.conectaPriceProgram')}<span>COP</span></div>
                <p>{t('bienestarHub.conectaPriceProgramDesc')}</p>
                <button type="button" className="bw-cta" onClick={() => goContact('conecta-programa')}>
                  {t('bienestarHub.conectaPriceCtaProgram')}
                </button>
              </article>
            </div>

            <div className="bw-faq">
              <details>
                <summary>{t('bienestarHub.conectaFaqWho')}</summary>
                <p>{t('bienestarHub.conectaFaqWhoAnswer')}</p>
              </details>
              <details>
                <summary>{t('bienestarHub.conectaFaqPay')}</summary>
                <p>{t('bienestarHub.conectaFaqPayAnswer')}</p>
              </details>
              <details>
                <summary>{t('bienestarHub.conectaFaqCancel')}</summary>
                <p>{t('bienestarHub.conectaFaqCancelAnswer')}</p>
              </details>
              <details>
                <summary>{t('bienestarHub.conectaFaqClinical')}</summary>
                <p>{t('bienestarHub.conectaFaqClinicalAnswer')}</p>
              </details>
            </div>
          </section>

          <BienestarProfessionalBlock variant="conecta" />

          <section className="bw-section bw-allies">
            <header className="bw-section-header">
              <h2 className="bw-section-title">{t('bienestarHub.alliesTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.alliesSub')}</p>
            </header>
            <button type="button" className="bw-cta" onClick={() => goContact('acompanamiento-aliado')}>
              {t('bienestarHub.alliesCta')}
            </button>
          </section>

          <BienestarAgendaSnippet serviceSlug="conecta-sesion" />
          <BienestarStoreCta variant="conecta" />
          <BienestarCrossPaths current="conecta" />
          <button type="button" className="bw-back-hub" onClick={() => navigateTo('/bienestar')}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> {t('bienestarLandings.backHub')}
          </button>
        </div>
      </main>
    </div>
  );
};

export default BienestarConectaPage;
